/**
 * Aparência do operador — tema (claro/escuro/sistema) + densidade.
 *
 * **Por que este arquivo existe.** O Briefing 1 V2 do Design System declara
 * desde a origem, no Eixo 3 (personalização por operador, Princípio 6):
 *
 *   §7.5.3 — «Tema ativo — Light / Dark / System»
 *   §5     — «Suporte a dark mode obrigatório — atendentes em uso noturno»
 *   §7.5.3 — «Design System provê tokens e padrões; persistência específica
 *             é responsabilidade do Code»
 *
 * Os tokens existiam (`[data-theme="dark"]` em `packages/design-system/src/tokens.css`,
 * 91 variáveis; `[data-density="compact"]`, 9 variáveis) e o Tailwind do app já
 * lia o seletor (`darkMode: ['selector', '[data-theme="dark"]']`). O que faltava
 * era o interruptor: em 12/Set/2026 a medição achou ZERO escritas de tema no
 * código dos três apps, e `index.html` fixava `data-theme="light"` no `<html>`.
 * Nenhum operador conseguia escolher. Este módulo é a metade que faltava.
 *
 * **Fronteira deliberada.** Aqui só mora lógica PURA (sem React, sem DOM global,
 * sem `window`): quem decide o tema efetivo, quem lê/escreve o storage, quem
 * normaliza payload de fora. O efeito colateral vive em `stores/appearance-store.ts`
 * (escolha do operador) e em `hooks/use-appearance.ts` (DOM + matchMedia + servidor).
 * A separação é o que torna o comportamento testável sem montar a aplicação.
 */

/** Chave do localStorage. Espelha a convenção de `auth-store` (`lume:auth`). */
export const APPEARANCE_STORAGE_KEY = 'lume:appearance';

/**
 * Namespace em `UserPreference` (tabela `user_preferences`, chave-valor
 * namespaced — PR S18). Keys: `theme` e `density`. Registrado na tabela de
 * namespaces reservados de `apps/engine/src/modules/user-preferences/README.md`,
 * que é o single-source desse registro.
 */
export const APPEARANCE_NAMESPACE = 'ui';
export const APPEARANCE_KEY_THEME = 'theme';
export const APPEARANCE_KEY_DENSITY = 'density';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type DensityChoice = 'comfortable' | 'compact';

export interface Appearance {
  theme: ThemeChoice;
  density: DensityChoice;
}

export const THEME_CHOICES: readonly ThemeChoice[] = ['light', 'dark', 'system'];
export const DENSITY_CHOICES: readonly DensityChoice[] = ['comfortable', 'compact'];

/**
 * Default CONSERVADOR, e a escolha é deliberada: `light`, não `system`.
 *
 * `system` seria o default «esperto», e viraria uma mudança visual não pedida
 * para todo operador cujo SO está em escuro — num tema que este app nunca
 * renderizou em produção. Princípio 15 do Caderno (defaults conservadores):
 * quem nunca escolheu continua vendo exatamente o que via ontem. `system` é
 * uma das três opções, e é do operador o gesto de pedi-la.
 */
export const DEFAULT_APPEARANCE: Appearance = { theme: 'light', density: 'comfortable' };

function isThemeChoice(v: unknown): v is ThemeChoice {
  return typeof v === 'string' && (THEME_CHOICES as readonly string[]).includes(v);
}

function isDensityChoice(v: unknown): v is DensityChoice {
  return typeof v === 'string' && (DENSITY_CHOICES as readonly string[]).includes(v);
}

/**
 * Tema EFETIVO. `data-theme` no `<html>` nunca recebe `system` — o
 * `tokens.css` só define paleta sob `[data-theme="light"]` e `[data-theme="dark"]`,
 * e um valor desconhecido deixaria a página sem NENHUMA cor definida.
 */
export function resolveTheme(choice: ThemeChoice, prefersDark: boolean): ResolvedTheme {
  if (choice === 'system') return prefersDark ? 'dark' : 'light';
  return choice;
}

/**
 * Aceita qualquer coisa e devolve uma `Appearance` válida, campo a campo.
 * Entrada vem de duas bordas não confiáveis: o localStorage (que o usuário
 * edita) e o `value` Json do `UserPreference` (que é `unknown` por contrato).
 */
export function normalizeAppearance(raw: unknown): Appearance {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_APPEARANCE };
  const obj = raw as Record<string, unknown>;
  return {
    theme: isThemeChoice(obj.theme) ? obj.theme : DEFAULT_APPEARANCE.theme,
    density: isDensityChoice(obj.density) ? obj.density : DEFAULT_APPEARANCE.density,
  };
}

/**
 * Aplica no elemento raiz. Recebe `root` e `prefersDark` como parâmetros (em vez
 * de ler `document`/`window`) para ser exercitável em teste sem tocar o ambiente.
 */
export function applyAppearance(root: Element, appearance: Appearance, prefersDark: boolean): void {
  root.setAttribute('data-theme', resolveTheme(appearance.theme, prefersDark));
  root.setAttribute('data-density', appearance.density);
}

/**
 * Lê do storage. NUNCA lança: em janela anônima, com site data bloqueado, ou
 * durante captura de thumbnail, o próprio acessor pode explodir.
 */
export function readStoredAppearance(storage: Storage): Appearance {
  try {
    const raw = storage.getItem(APPEARANCE_STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_APPEARANCE };
    return normalizeAppearance(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
}

/** Escreve no storage. NUNCA lança — vide `readStoredAppearance`. */
export function writeStoredAppearance(storage: Storage, appearance: Appearance): void {
  try {
    storage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    /* preferência visual não vale derrubar a aplicação */
  }
}
